import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  RestoreFromTrash as TrashIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import PensumService from '../../services/pensum.service';
import GradeService from '../../services/grade.service';

const PensumList = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [pensums, setPensums] = useState([]);
  const [groupedPensums, setGroupedPensums] = useState({});
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedGrade) {
      loadPensumsByGrade(selectedGrade);
    } else {
      loadAllPensums();
    }
  }, [selectedGrade]);

  const loadData = async () => {
    try {
      const gradesRes = await GradeService.getAll();
      setGrades(gradesRes.data?.data || []);
      loadAllPensums();
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Error loading data', 'error');
    }
  };

  const loadAllPensums = async () => {
    setLoading(true);
    try {
      const response = await PensumService.getByGradeGrouped();
      const grouped = response.data?.data || {};
      setGroupedPensums(grouped);
      
      // Flatten for search
      const allPensums = Object.values(grouped).flat();
      setPensums(allPensums);
    } catch (error) {
      console.error('Error fetching pensums:', error);
      showSnackbar('Error loading pensums: ' + (error.response?.data?.message || error.message), 'error');
      setPensums([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPensumsByGrade = async (gradeId) => {
    setLoading(true);
    try {
      const response = await PensumService.getByGrade(gradeId);
      setPensums(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching pensums:', error);
      showSnackbar('Error loading pensums', 'error');
      setPensums([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await PensumService.delete(deleteDialog.id);
      showSnackbar('Pensum deleted successfully');
      if (selectedGrade) {
        loadPensumsByGrade(selectedGrade);
      } else {
        loadAllPensums();
      }
    } catch (error) {
      showSnackbar('Error deleting pensum', 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredPensums = pensums.filter(pensum => {
    const matchesSearch = 
      pensum.curso?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pensum.grade?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getGradeName = (gradeId) => {
    const grade = grades.find(g => g.id === gradeId);
    return grade ? grade.name : `${t('pensums.grade')} ${gradeId}`;
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('pensums.listTitle')}</Typography>
            <Box display="flex" gap={1}>
              {hasPermission('pensums.view') && (
                <Button
                  variant="outlined"
                  startIcon={<TrashIcon />}
                  onClick={() => navigate('/pensums/deleted')}
                >
                  {t('pensums.deletedPensums')}
                </Button>
              )}
              {hasPermission('pensums.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/pensums/new')}
                >
                  {t('pensums.newPensum')}
                </Button>
              )}
            </Box>
          </Box>

          <Box display="flex" gap={2} mb={2}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('pensums.filterByGrade')}</InputLabel>
              <Select
                value={selectedGrade}
                label={t('pensums.filterByGrade')}
                onChange={(e) => setSelectedGrade(e.target.value)}
              >
                <MenuItem value="">{t('pensums.allGrades')}</MenuItem>
                {grades.map((grade) => (
                  <MenuItem key={grade.id} value={grade.id}>
                    {grade.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              placeholder={t('pensums.searchPensums')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, maxWidth: 400 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Box>

          {selectedGrade ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('pensums.order')}</TableCell>
                    <TableCell>{t('pensums.curso')}</TableCell>
                    <TableCell>{t('pensums.grade')}</TableCell>
                    <TableCell align="center">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredPensums.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {t('pensums.noPensumsFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPensums.map((pensum) => (
                    <TableRow key={pensum.id} hover>
                      <TableCell>
                        <Chip label={pensum.orden} color="primary" size="small" />
                      </TableCell>
                      <TableCell>{pensum.curso?.name || 'N/A'}</TableCell>
                      <TableCell>{pensum.grade?.name || 'N/A'}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {hasPermission('pensums.edit') && (
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/pensums/${pensum.grade_id}/edit`)}
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                            {hasPermission('pensums.delete') && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(pensum.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box>
              {Object.keys(groupedPensums).map((gradeId) => (
                <Accordion key={gradeId} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={2}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6">{getGradeName(parseInt(gradeId))}</Typography>
                        {hasPermission('pensums.edit') && (
                          <IconButton
                            size="small"
                            color="primary"
                            title={t('pensums.editPensumTitle')}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/pensums/${gradeId}/edit`);
                            }}
                            sx={{ p: 0.5 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Chip 
                        label={t('pensums.cursosCount', { count: groupedPensums[gradeId].length })} 
                        color="primary" 
                        size="small"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
<TableHead>
                        <TableRow>
                            <TableCell>{t('pensums.order')}</TableCell>
                            <TableCell>{t('pensums.curso')}</TableCell>
                            <TableCell align="center">{t('common.actions')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupedPensums[gradeId]
                            .filter(pensum => {
                              const matchesSearch = 
                                pensum.curso?.name?.toLowerCase().includes(searchTerm.toLowerCase());
                              return matchesSearch;
                            })
                            .map((pensum) => (
                            <TableRow key={pensum.id} hover>
                              <TableCell>
                                <Chip label={pensum.orden} color="primary" size="small" />
                              </TableCell>
                              <TableCell>{pensum.curso?.name || 'N/A'}</TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                  {hasPermission('pensums.edit') && (
                                    <IconButton
                                      size="small"
                                      onClick={() => navigate(`/pensums/${pensum.grade_id}/edit`)}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  )}
                                  {hasPermission('pensums.delete') && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDelete(pensum.id)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>{t('pensums.deletePensum')}</DialogTitle>
        <DialogContent>
          <Typography>{t('pensums.deletePensumConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PensumList;
